import { toMillis } from "./explorerFilterUtils";

/**
 * Candidate field keys for each logical attendance dimension. These mirror the
 * flexible field names already handled across the payroll helpers so the smart
 * filters work regardless of how a punch document was written.
 */
export const ATTENDANCE_FIELD_KEYS = {
  employeeId: ["employeeId", "empId", "cardNumber", "searchEmployee", "employeeCode", "empCode", "uid", "user_id", "userId", "code", "EmpId"],
  employeeName: ["employeeName", "name", "fullName", "empName", "Name", "EmployeeName"],
  device: ["machineType", "deviceModel", "deviceName", "device_name", "deviceSerial", "device", "deviceId", "Device", "machine", "machineId", "terminal", "reader"],
  branch: ["branchName", "branch", "searchBranch", "Branch", "branchId", "shop", "outlet"],
  punchType: ["attendanceStatus", "type", "Type", "punchType", "PunchType", "direction", "Direction", "inOut", "io"],
  status: ["status", "Status", "state"],
  timestamp: [
    "timestamp",
    "punchTime",
    "punchDateTime",
    "searchDate",
    "date",
    "Date",
    "attendanceDate",
    "createdAt",
    "inTime",
    "checkInTime",
    "checkIn",
    "timeStamp",
    "dateTime",
  ],
};

export const DEFAULT_ATTENDANCE_STATE = {
  employeeId: "",
  employeeName: "",
  device: "",
  branch: "",
  punchType: "",
  status: "",
  fromDate: "",
  toDate: "",
  special: null,
};

function firstDefined(data, keys) {
  for (const key of keys) {
    const v = data?.[key];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

export function getAttendanceField(row, dimension) {
  return firstDefined(row?.data || {}, ATTENDANCE_FIELD_KEYS[dimension] || []);
}

export function getPunchMillis(row) {
  return toMillis(getAttendanceField(row, "timestamp"));
}

/** Normalize a punch type value to IN / OUT / other. */
export function normalizePunchType(value) {
  const s = String(value ?? "").trim().toLowerCase();
  if (!s) return "";
  if (["in", "i", "0", "checkin", "check-in", "check in", "duty on", "on"].includes(s) || s.includes("in")) {
    if (s.includes("out")) return "OUT";
    return "IN";
  }
  if (["out", "o", "1", "checkout", "check-out", "check out", "duty off", "off"].includes(s) || s.includes("out")) {
    return "OUT";
  }
  return value ? String(value).toUpperCase() : "";
}

function startOfDayMillis(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function endOfDayMillis(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function textIncludes(value, needle) {
  if (!needle) return true;
  return String(value ?? "").toLowerCase().includes(String(needle).toLowerCase());
}

/** Group rows that share the same employee + same minute (duplicate punches). */
function duplicateRowIds(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const emp = String(getAttendanceField(row, "employeeId") ?? "");
    const ms = getPunchMillis(row);
    if (ms == null) return;
    const key = `${emp}|${Math.floor(ms / 60000)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row.id);
  });
  const ids = new Set();
  groups.forEach((list) => {
    if (list.length > 1) list.forEach((id) => ids.add(id));
  });
  return ids;
}

/** Flag first IN punch per employee/day that happens after a late threshold. */
function lateRowIds(rows, thresholdMinutes = 9 * 60 + 15) {
  const firstInByKey = new Map();
  rows.forEach((row) => {
    if (normalizePunchType(getAttendanceField(row, "punchType")) !== "IN") return;
    const ms = getPunchMillis(row);
    if (ms == null) return;
    const emp = String(getAttendanceField(row, "employeeId") ?? "");
    const day = new Date(ms);
    const dayKey = `${emp}|${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
    const existing = firstInByKey.get(dayKey);
    if (!existing || ms < existing.ms) firstInByKey.set(dayKey, { ms, id: row.id });
  });
  const ids = new Set();
  firstInByKey.forEach(({ ms, id }) => {
    const d = new Date(ms);
    const minutes = d.getHours() * 60 + d.getMinutes();
    if (minutes > thresholdMinutes) ids.add(id);
  });
  return ids;
}

/**
 * Apply the attendance quick filters (all AND) to loaded rows.
 * Special presets (duplicate / late) are computed across the loaded set.
 */
export function applyAttendanceFilters(rows, state) {
  const s = { ...DEFAULT_ATTENDANCE_STATE, ...(state || {}) };
  let specialIds = null;
  if (s.special === "duplicate") specialIds = duplicateRowIds(rows);
  else if (s.special === "late") specialIds = lateRowIds(rows);

  const from = startOfDayMillis(s.fromDate);
  const to = endOfDayMillis(s.toDate);
  const punchWanted = s.punchType ? normalizePunchType(s.punchType) : "";

  return rows.filter((row) => {
    if (specialIds && !specialIds.has(row.id)) return false;
    if (s.employeeId && !textIncludes(getAttendanceField(row, "employeeId"), s.employeeId)) return false;
    if (s.employeeName && !textIncludes(getAttendanceField(row, "employeeName"), s.employeeName)) return false;
    if (s.device && !textIncludes(getAttendanceField(row, "device"), s.device)) return false;
    if (s.branch && !textIncludes(getAttendanceField(row, "branch"), s.branch)) return false;
    if (s.status && !textIncludes(getAttendanceField(row, "status"), s.status)) return false;
    if (punchWanted && normalizePunchType(getAttendanceField(row, "punchType")) !== punchWanted) return false;
    if (from != null || to != null) {
      const ms = getPunchMillis(row);
      if (ms == null) return false;
      if (from != null && ms < from) return false;
      if (to != null && ms > to) return false;
    }
    return true;
  });
}

export function isAttendanceActive(state) {
  const s = { ...DEFAULT_ATTENDANCE_STATE, ...(state || {}) };
  return Boolean(
    s.employeeId || s.employeeName || s.device || s.branch || s.punchType || s.status || s.fromDate || s.toDate || s.special
  );
}

/* ------------------------------------------------------------------ */
/* Quick presets                                                       */
/* ------------------------------------------------------------------ */

function ymd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function todayRange() {
  const now = new Date();
  const key = ymd(now);
  return { fromDate: key, toDate: key };
}

function yesterdayRange() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const key = ymd(d);
  return { fromDate: key, toDate: key };
}

function thisWeekRange() {
  const now = new Date();
  const day = now.getDay(); // 0 Sun ... 6 Sat
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((day + 6) % 7));
  return { fromDate: ymd(monday), toDate: ymd(now) };
}

function thisMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { fromDate: ymd(start), toDate: ymd(now) };
}

/**
 * Attendance quick presets. `build()` returns a partial attendance state that
 * is merged onto the current one when the chip is clicked.
 */
export const ATTENDANCE_QUICK_PRESETS = [
  { id: "today", label: "Today's Attendance", build: () => ({ ...DEFAULT_ATTENDANCE_STATE, ...todayRange() }) },
  { id: "yesterday", label: "Yesterday", build: () => ({ ...DEFAULT_ATTENDANCE_STATE, ...yesterdayRange() }) },
  { id: "week", label: "This Week", build: () => ({ ...DEFAULT_ATTENDANCE_STATE, ...thisWeekRange() }) },
  { id: "month", label: "This Month", build: () => ({ ...DEFAULT_ATTENDANCE_STATE, ...thisMonthRange() }) },
  { id: "main", label: "Main Branch", build: () => ({ ...DEFAULT_ATTENDANCE_STATE, branch: "Main" }) },
  { id: "hikevision", label: "HIKEVISION", build: () => ({ ...DEFAULT_ATTENDANCE_STATE, device: "HIKEVISION" }) },
  { id: "duplicate", label: "Duplicate Punches", build: () => ({ ...DEFAULT_ATTENDANCE_STATE, special: "duplicate" }) },
  { id: "late", label: "Late Employees", build: () => ({ ...DEFAULT_ATTENDANCE_STATE, special: "late" }) },
];
